import React, { useState, useEffect, useCallback } from 'react';
import { getDevices, addDevice, deleteDevice } from '../api';
import './DevicesPage.css';

function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Форма добавления
  const [newUid, setNewUid] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [adding, setAdding] = useState(false);
  
  // Модальное окно удаления
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await getDevices();
      setDevices(result.devices || []);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки устройств');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleAddDevice = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!newUid.trim() || !newPassword.trim()) {
      setError('UID и пароль обязательны');
      return;
    }
    
    setAdding(true);
    
    try {
      await addDevice(newUid.trim(), newPassword);
      setSuccess(`Устройство "${newUid.trim()}" добавлено`);
      setNewUid('');
      setNewPassword('');
      await fetchDevices();
    } catch (err) {
      setError(err.message || 'Ошибка добавления устройства');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteDevice = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    setError('');
    setSuccess('');
    
    try {
      await deleteDevice(deleteConfirm);
      setSuccess(`Устройство "${deleteConfirm}" удалено`);
      setDeleteConfirm(null);
      await fetchDevices();
    } catch (err) {
      setError(err.message || 'Ошибка удаления устройства');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="devices-page">
      <div className="page-header">
        <h1>Устройства</h1>
        <div className="page-header-actions">
          <button className="secondary" onClick={fetchDevices} disabled={loading}>
            Обновить
          </button>
        </div>
      </div>

      {/* Форма добавления */}
      <div className="add-device-form">
        <h3>Добавить устройство</h3>
        <form onSubmit={handleAddDevice}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="uid">UID устройства</label>
              <input
                type="text"
                id="uid"
                value={newUid}
                onChange={(e) => setNewUid(e.target.value)}
                placeholder="например: piano_001"
                disabled={adding}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Пароль</label>
              <input
                type="password"
                id="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="минимум 6 символов"
                disabled={adding}
              />
            </div>
            <button type="submit" className="primary" disabled={adding}>
              {adding ? 'Добавление...' : 'Добавить'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading ? (
        <div className="loading">Загрузка устройств</div>
      ) : devices.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📱</div>
            <p>Устройства не зарегистрированы</p>
          </div>
        </div>
      ) : (
        <>
          <div className="devices-stats">
            Всего устройств: {devices.length}
          </div>
          
          <div className="devices-table-container">
            <table className="devices-table">
              <thead>
                <tr>
                  <th>UID устройства</th>
                  <th>Пароль</th>
                  <th className="col-actions">Действия</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.uid}>
                    <td className="col-uid">{device.uid}</td>
                    <td className="col-password">
                      <span className="password-mask">••••••••</span>
                    </td>
                    <td className="col-actions">
                      <button
                        className="danger btn-delete"
                        onClick={() => setDeleteConfirm(device.uid)}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Модальное окно подтверждения удаления */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Подтверждение удаления</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Вы уверены, что хотите удалить устройство <strong>{deleteConfirm}</strong>?</p>
              <p className="text-muted">Устройство потеряет доступ к серверу.</p>
            </div>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Отмена
              </button>
              <button className="danger" onClick={handleDeleteDevice} disabled={deleting}>
                {deleting ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DevicesPage;